// IntentBadge.tsx — surfaces IntentVector V-axis on Live page (M0.5)
//   + M2 scene_symbols primary (FE_SPEC §7).
//
// iter_3 fix: until now FE never displayed which "议会方向" (V axis: converge /
// diverge / diagnose / resolve / validate / project) the user picked at session
// creation. With this badge, the Live page header makes the council's cognitive
// shape visible — answering "what is this council actually doing?".
//
// M2 / FE-A extension (FE_SPEC §7):
//   - scene_symbols[] supplied → primary = max confidence; tooltip lists all.
//   - read-only invariant preserved (no onClick / onSymbolClick).
//   - ★ P0-9 闭环: quality_compromised=true → dim-grey ring (ash border + opacity)
//
// Props are nullable so legacy non-vector sessions (M0 baseline) can render
// nothing (returns null). Callers should also gate on FYD_INTENT_MODEL_V2 if
// they want to hide for prod that has the flag off.

import { useTranslation } from 'react-i18next';
import type { IntentVector } from '@/types/intent';
import { intentGlyph, intentLabelKey } from '@/ui/utils/intent_glyph';

export type SceneSymbolId =
  | 'decision_high_stakes'
  | 'decision_low_stakes'
  | 'technical_evaluation'
  | 'creative_exploration'
  | 'risk_analysis'
  | 'emotional_processing'
  | 'value_clarification'
  | 'strategy_planning'
  | 'conflict_resolution'
  | 'knowledge_synthesis'
  | 'unknown_council';

export interface SceneSymbol {
  id: SceneSymbolId;
  confidence: number; // [0,1]
}

interface IntentBadgeProps {
  intent?: IntentVector | null;
  /** ★ M2 scene_symbols (FE_SPEC §7); primary = max confidence. */
  scene_symbols?: SceneSymbol[];
  /** ★ P0-9 闭环 (PRD §7.10.9): dim-grey ring when council quality flagged. */
  quality_compromised?: boolean;
  className?: string;
}

export default function IntentBadge({
  intent,
  scene_symbols,
  quality_compromised = false,
  className = '',
}: IntentBadgeProps) {
  const { t } = useTranslation();

  const v = intent?.V;
  if (!v) return null; // legacy / unknown — hide

  const label = t(intentLabelKey(v));
  const glyph = intentGlyph(v);

  // ★ scene_symbols primary by max confidence (FE_SPEC §7).
  const sortedSymbols = (scene_symbols ?? [])
    .slice()
    .sort((a, b) => b.confidence - a.confidence);
  const primary = sortedSymbols[0];
  const sceneLabel = primary
    ? t(`intent.scene.${primary.id}`, { defaultValue: primary.id })
    : null;

  // Tooltip: list every scene_symbol id when ≥2 (read-only · informational).
  const tooltip =
    sortedSymbols.length >= 2
      ? sortedSymbols.map((s) => `${s.id}:${s.confidence.toFixed(2)}`).join(' · ')
      : t('intent.intentLabel');

  // ★ P0-9 闭环: ash border + reduced opacity when quality_compromised.
  const qualityClass = quality_compromised
    ? 'border-ash opacity-70'
    : 'border-hairline';

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-md bg-paper ${qualityClass} ${className}`}
      data-testid="intent-badge"
      data-intent-v={v}
      data-scene-symbol-primary={primary?.id ?? ''}
      data-quality-compromised={quality_compromised ? 'true' : 'false'}
      title={tooltip}
    >
      <span className="font-mono text-sm text-ink-2" aria-hidden>
        {glyph}
      </span>
      <span className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
        {t('intent.intentLabel')}
      </span>
      <span className="font-serif text-sm text-ink">{label}</span>
      {sceneLabel && (
        <>
          <span className="text-ink-3 font-mono text-xs" aria-hidden>
            ·
          </span>
          <span
            className="font-serif text-sm text-ink"
            data-testid="intent-badge-scene"
          >
            {sceneLabel}
          </span>
        </>
      )}
    </div>
  );
}
